import React from 'react';
import { supabase } from '../supabase';
import { variationsOf } from '../data/devotionalYear';

// Edit the daily devotional, three languages at a time.
//
// A day rotates over however many distinct devotionals are written for it, and
// that count is per language: adding one to a day's English leaves its Chinese
// alone. Fields sit side by side so a missing or lagging translation is
// obvious rather than something you have to go looking for.
//
// Saving goes through the save-daily-bread function, never straight to the
// table — daily_bread is read-only to the browser key, and the function decides
// whether the caller is an admin from their Google session.

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'zh', label: '繁體 Traditional' },
  { code: 'sc', label: '简体 Simplified' },
];

// verses_ref is deliberately absent. It only ever held the New Testament
// reference, repeating what verses.nt_title already says, and nothing renders
// it — the reading's portions come from the verses table, which the Reading
// page shows in both Testaments.
const FIELDS = [
  { key: 'topic',      label: 'Topic',            rows: 1 },
  { key: 'key_verse',  label: 'Key verse',        rows: 3 },
  { key: 'emphasis',   label: 'Emphasis',         rows: 3 },
  { key: 'musing',     label: 'Musing',           rows: 5 },
  { key: 'prayer',     label: 'Prayer',           rows: 3 },
  { key: 'hymn_title', label: 'Hymn title',       rows: 1 },
  { key: 'hymn_url',   label: 'Hymn link',        rows: 1 },
  { key: 'hymn_audio', label: 'Hymn audio',       rows: 1 },
  { key: 'hymn_text',  label: 'Hymn text',        rows: 6 },
  { key: 'ls_title',   label: 'Life-study title', rows: 2 },
  { key: 'ls_url',     label: 'Life-study link',  rows: 1 },
  { key: 'ls_audio',   label: 'Life-study audio', rows: 1 },
];

const NEW = 'new';
const blank = () => Object.fromEntries(FIELDS.map(f => [f.key, '']));

export default function DailyBreadAdmin() {
  const today = new Date().toLocaleDateString('en-CA');
  const [date, setDate] = React.useState(today);
  const [rows, setRows] = React.useState(null);
  const [year, setYear] = React.useState(null);
  const [draft, setDraft] = React.useState(() => ({ en: blank(), zh: blank(), sc: blank() }));
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState('');

  const md = date.slice(5);

  React.useEffect(() => {
    let cancelled = false;
    setRows(null); setNote('');
    supabase.from('daily_bread').select('*').eq('md', md).order('year', { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setNote('Could not load this day.'); setRows([]); return; }
        setRows(data || []);
      });
    return () => { cancelled = true; };
  }, [md]);

  const years = React.useMemo(
    () => [...new Set((rows || []).map(r => r.year))].sort((a, b) => a - b), [rows]);

  // settle on a variation once the day loads, and reset when the day changes
  React.useEffect(() => {
    if (!rows) return;
    setYear(prev => (prev !== NEW && years.includes(prev) ? prev : (years[0] ?? NEW)));
  }, [rows, years]);

  // fill the form from the chosen variation. A new one starts from the most
  // recent writing rather than from nothing — a starting point to edit down.
  React.useEffect(() => {
    if (!rows || year === null) return;
    const next = {};
    for (const { code } of LANGS) {
      const mine = rows.filter(r => r.lang === code);
      const src = year === NEW
        ? mine[mine.length - 1]
        : mine.find(r => r.year === year);
      next[code] = Object.fromEntries(FIELDS.map(f => [f.key, src?.[f.key] ?? '']));
    }
    setDraft(next);
  }, [rows, year]);

  const storedFor = (code) =>
    year === NEW ? null : (rows || []).find(r => r.lang === code && r.year === year) || null;

  const dirty = (code) => {
    const stored = storedFor(code);
    if (year === NEW) return FIELDS.some(f => (draft[code]?.[f.key] || '').trim() !== '');
    if (!stored) return FIELDS.some(f => (draft[code]?.[f.key] || '').trim() !== '');
    return FIELDS.some(f => String(draft[code]?.[f.key] ?? '') !== String(stored[f.key] ?? ''));
  };

  const changed = LANGS.filter(l => dirty(l.code));

  async function save() {
    if (!changed.length || busy) return;
    setBusy(true); setNote('');

    const { data: sess } = await supabase.auth.getSession();
    const token = sess?.session?.access_token;
    if (!token) {
      setNote('Editing needs a Google sign-in. Sign out and sign back in with Google.');
      setBusy(false);
      return;
    }

    const results = [];
    for (const { code, label } of changed) {
      const stored = storedFor(code);
      const body = {
        mode: stored ? 'correct' : 'add',
        md, lang: code, year: stored ? year : undefined,
        fields: draft[code],
      };
      try {
        const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/save-daily-bread`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        });
        const out = await res.json();
        results.push(res.ok ? `${label} ${out.mode === 'add' ? 'added' : 'saved'}`
                            : `${label} failed — ${out.error || res.status}`);
      } catch (err) {
        console.error('save-daily-bread failed', err);
        results.push(`${label} failed`);
      }
    }
    setNote(results.join(' · '));

    const { data } = await supabase.from('daily_bread')
      .select('*').eq('md', md).order('year', { ascending: true });
    setRows(data || []);
    setBusy(false);
  }

  if (!rows) return <div className="loading">Loading…</div>;

  return (
    <div className="db-admin">
      <div className="db-admin-bar">
        <input type="date" className="date-input" value={date}
               onChange={e => setDate(e.target.value)} />
        <div className="db-admin-years">
          {years.map(y => (
            <button key={y} className={`db-year${year === y ? ' active' : ''}`}
                    onClick={() => setYear(y)}>{y}</button>
          ))}
          <button className={`db-year db-year-new${year === NEW ? ' active' : ''}`}
                  onClick={() => setYear(NEW)}>+ New variation</button>
        </div>
      </div>

      <p className="db-admin-hint">
        {year === NEW
          ? 'A new devotional for this day, pre-filled from the most recent one. Whichever languages you fill get one added — the others keep the rotation they have.'
          : `Editing the ${year} devotional. Saving corrects it in place; the rotation stays the same length.`}
      </p>

      <div className="db-admin-rot">
        {LANGS.map(({ code, label }) => {
          const mine = rows.filter(r => r.lang === code);
          const n = variationsOf(mine).length;
          return (
            <span key={code} className="db-rot-chip">
              {/* A language with no devotional at all is not a zero-length
                  rotation — say so plainly rather than counting nothing. */}
              {n === 0
                ? `${label}: nothing written yet`
                : `${label}: rotates every ${n} year${n === 1 ? '' : 's'}`}
              {n > 0 && mine.length !== n && <em> ({mine.length} rows, {mine.length - n} duplicate)</em>}
            </span>
          );
        })}
      </div>

      <div className="db-admin-grid">
        <div className="db-admin-head">
          <span />
          {LANGS.map(l => <span key={l.code}>{l.label}</span>)}
        </div>
        {FIELDS.map(f => (
          <div className="db-admin-row" key={f.key}>
            <label className="db-admin-label">{f.label}</label>
            {LANGS.map(({ code }) => (
              <textarea
                key={code}
                className="db-admin-input"
                rows={f.rows}
                value={draft[code]?.[f.key] ?? ''}
                onChange={e => setDraft(d => ({ ...d, [code]: { ...d[code], [f.key]: e.target.value } }))}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="db-admin-actions">
        <button className="start-btn" onClick={save} disabled={!changed.length || busy}>
          {busy ? 'Saving…'
                : changed.length ? `Save ${changed.map(l => l.label.split(' ')[0]).join(', ')}`
                : 'No changes'}
        </button>
        {note && <span className="db-admin-note">{note}</span>}
      </div>
    </div>
  );
}
